import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import AvailableLimitMeter from '../components/AvailableLimitMeter';
import ManualItemForm from '../components/ManualItemForm';
import SubmitButton from '../components/SubmitButton';
import useListenSize from '../hooks/useListenSize';
import useShopifyApi from '../hooks/useShopifyApi';
import {
  addInStoreCartItem,
  updateInStoreCartItem,
  removeInStoreCartItem,
  addCheckoutAll,
  addPayFrequencyLookup,
  addVwoDecision,
  changeFeatureLinearFlow,
  addVerificationCheckoutUid,
  sendError,
} from '../redux/actions';
import buildInStoreInitializePayload from '../helpers/buildInStoreInitializePayload';
import { formatCurrency, formatCurrencyCompact } from '../helpers/convertToCurrency';
import getApprovalExpiration from '../helpers/getApprovalExpiration';
import { getApprovalLimit } from '../helpers/inStoreSelectors';
import { segmentEvent } from '../helpers/sendEvent';
import emptyCartIcon from '../assets/img/cart-empty.svg';
import editIcon from '../assets/img/edit-pencil.svg';
import removeIcon from '../assets/img/remove-x.svg';

// Tag scanning is its own step in the in-store flow and isn't built yet - this path
// currently falls through to the router's catch-all.
const SCAN_PRODUCT_TAG_PATH = '/scan-product-tag';

// Once /initialize accepts the cart, the in-store flow joins the shared pricing step,
// the same place NewUserApproved sends shoppers after its own initialize.
const CONTINUE_PATH = '/display-offered-lease-details';

const TYPE_IT_IN_BUTTON_ID = 'k-approved-cart-type-it-in';
const editButtonId = itemId => `k-approved-cart-edit-${itemId}`;

const toCents = amount => Math.round((Number(amount) || 0) * 100);

const getQuantity = item => Number(item?.quantity) || 1;

const getItemTotal = item => (Number(item?.price) || 0) * getQuantity(item);

// A warranty is bought per unit, as in the tele-sales agent cart. Older persisted items
// stored `warranty` as a bare boolean with no price, which counts as nothing.
const getWarrantyTotal = item => (Number(item?.warranty?.price) || 0) * getQuantity(item);

const getLineTotal = item => getItemTotal(item) + getWarrantyTotal(item);

// "Make / Model · Description", as typed. Either half can be missing on older items.
const getItemName = item => [item.makeModel, item.description].filter(Boolean).join(' · ');

const getItemMeta = item => [item.sku && `SKU# ${item.sku}`, `Qty: ${getQuantity(item)}`].filter(Boolean).join(' · ');

const getWarrantyMeta = item => {
  const { name, sku } = item.warranty;
  return [`Warranty: ${name}`, sku && `SKU# ${sku}`, formatCurrency(getWarrantyTotal(item))]
    .filter(Boolean)
    .join(' · ');
};

/**
 * In-store VCC: the shopper is approved and builds a cart against their limit.
 * Figma: In-Store VCC Flows (NFM) - "7. Approved with cart".
 *   574:762  empty cart, "Type it in" collapsed
 *   574:834  "Type it in" expanded into the item-details form
 *   574:948  cart with items
 *   133:22483  with the EnableTagScanning gate on (adds "Scan product tag")
 *
 * One item form is open at a time: adding a new item, or editing one in place.
 */
function ApprovedWithCart() {
  const store = useSelector(state => ({
    approvalLimit: getApprovalLimit(state),
    checkout: state.loanInfo?.checkout,
    inStoreCart: state.loanInfo?.inStoreCart,
    featureGates: state.technical?.featureGates,
    newUserDetails: state.personal?.newUserDetails,
    phone: state.contact?.phone,
    verificationCode: state.loanInfo?.verificationCode,
    DEdgeToken: state.technical?.DEdgeToken,
  }));
  const dispatch = useDispatch();
  const { request, response, loading } = useShopifyApi();
  const { handleSubmit } = useForm();
  const history = useHistory();
  // null when closed, { mode: 'add' }, or { mode: 'edit', id }.
  const [editor, setEditor] = useState(null);
  // Where focus goes when the open form closes - the control that opened it.
  const returnFocusToRef = useRef(null);
  // Where focus goes after a row is removed.
  const pendingFocusRef = useRef(null);

  const cartItems = store.inStoreCart || [];
  const isCartEmpty = cartItems.length === 0;
  // Money is summed and subtracted in whole cents, so the meter, the cart Total, the
  // footer and the form's limit check all agree to the cent.
  const cartTotal = cartItems.reduce((total, item) => total + toCents(getLineTotal(item)), 0) / 100;
  const { approvalLimit } = store;
  const remaining = Math.max(toCents(approvalLimit) - toCents(cartTotal), 0) / 100;
  const expiresOn = getApprovalExpiration(store.checkout?.approval_expires_at);
  const canScanProductTag = Boolean(store.featureGates?.EnableTagScanning);
  const isAdding = editor?.mode === 'add';
  const editingId = editor?.mode === 'edit' ? editor.id : null;

  // Both the cart and the inline form change the page height inside the host iframe.
  useListenSize(`${cartItems.length}-${editor?.mode}-${editingId}`);

  useEffect(() => {
    segmentEvent('page', 'Approved With Cart', { cart_total: cartTotal, cart_item_count: cartItems.length });
  }, []);

  // A closing form takes its opener with it, so hand focus back explicitly rather
  // than letting keyboard and screen reader users fall to the top of the page.
  useEffect(() => {
    if (!editor && returnFocusToRef.current) {
      document.getElementById(returnFocusToRef.current)?.focus();
      returnFocusToRef.current = null;
    }
  }, [editor]);

  useEffect(() => {
    if (pendingFocusRef.current) {
      document.getElementById(pendingFocusRef.current)?.focus();
      pendingFocusRef.current = null;
    }
  }, [cartItems.length]);

  const closeEditor = () => {
    returnFocusToRef.current = editingId ? editButtonId(editingId) : TYPE_IT_IN_BUTTON_ID;
    setEditor(null);
  };

  const onAddItem = item => {
    dispatch(addInStoreCartItem(item));
    segmentEvent('track', 'Instore item added to cart', {
      entry_method: 'manual',
      item_total: getLineTotal(item),
      warranty: Boolean(item.warranty),
    });
    closeEditor();
  };

  const onRemoveItem = item => {
    const index = cartItems.findIndex(cartItem => cartItem.id === item.id);
    // The removed row's button disappears with it, so move focus to the row that slides
    // into its place (or the one before it), and to "Type it in" once the cart is empty.
    const neighbour = cartItems[index + 1] || cartItems[index - 1];
    pendingFocusRef.current = neighbour ? editButtonId(neighbour.id) : TYPE_IT_IN_BUTTON_ID;
    dispatch(removeInStoreCartItem(item.id));
    segmentEvent('track', 'In Store Item Removed', { item_total: getLineTotal(item) });
  };

  const onSaveItem = changes => {
    dispatch(updateInStoreCartItem(editingId, changes));
    segmentEvent('track', 'In Store Item Edited', {
      item_total: getLineTotal(changes),
      warranty: Boolean(changes.warranty),
    });
    closeEditor();
  };

  // Opens the lease with the cart the shopper just built. Mirrors NewUserApproved's
  // handling of the initialize response, so every screen after this sees the same state.
  const onSubmit = async () => {
    segmentEvent('track', 'Proceed To Checkout', { cart_total: cartTotal, cart_item_count: cartItems.length });

    const payload = buildInStoreInitializePayload({
      newUserDetails: store.newUserDetails,
      phone: store.phone,
      cart: cartItems,
      code: store.verificationCode,
      dedgeToken: store.DEdgeToken,
    });
    await request.initializeRequest(payload);
    const data = await response.json();

    if (response.ok && data?.new_decision?.approved === false) {
      history.push('/not-approved');
    } else if (response.ok) {
      dispatch(addCheckoutAll(data));
      dispatch(addPayFrequencyLookup({ first_name: store.newUserDetails?.billing_first_name }));
      dispatch(addVwoDecision({ feature: 'offerPage', variation: 'Version-B' }));
      dispatch(addVwoDecision({ feature: 'checkoutPage', variation: 'Version-A' }));
      dispatch(changeFeatureLinearFlow(false));
      dispatch(addVerificationCheckoutUid(data.uid));
      if (data?.reason?.includes('existing non-performing lease')) {
        history.push('/due-payment');
      } else {
        history.push(CONTINUE_PATH);
      }
    } else if (data?.message?.includes('Shopping cart exceeds available limit')) {
      dispatch(sendError(data));
      history.push('/exceeded-limit');
    } else if (data?.message?.includes('Minimum cart')) {
      // e.g. "Minimum cart should be $300.00. Please add an item(s) in order to proceed to
      // checkout." Shown on the error page in place of its generic copy.
      history.push('/error', { message: data.message });
    } else {
      history.push('/error');
    }
  };

  const renderCartItem = item => {
    if (item.id === editingId) {
      return (
        <li key={item.id} className="k-cart-card__item k-cart-card__item--editing">
          <ManualItemForm
            // Editing replaces this item's total rather than adding to the cart's.
            remaining={remaining + getLineTotal(item)}
            initialValues={item}
            title="Edit item"
            submitLabel="Save changes"
            onSave={onSaveItem}
            onCancel={closeEditor}
          />
        </li>
      );
    }

    return (
      <li key={item.id} className="k-cart-card__item">
        <div className="k-cart-card__item-info">
          <p className="k-cart-card__item-name">{getItemName(item)}</p>
          <p className="k-cart-card__item-meta">{getItemMeta(item)}</p>
          {item.warranty?.name && <p className="k-cart-card__item-meta">{getWarrantyMeta(item)}</p>}
        </div>
        <div className="k-cart-card__item-side">
          {/* The item on its own; its warranty is priced on its own line, and both count
              toward the Total below. */}
          <p className="k-cart-card__item-price">{formatCurrency(getItemTotal(item))}</p>
          <button
            id={editButtonId(item.id)}
            type="button"
            className="k-cart-card__edit"
            aria-label={`Edit ${getItemName(item)}`}
            onClick={() => setEditor({ mode: 'edit', id: item.id })}
          >
            <span>Edit</span>
            <img className="k-cart-card__edit-icon" src={editIcon} alt="" />
          </button>
        </div>
        <button
          type="button"
          className="k-cart-card__remove"
          aria-label={`Remove ${getItemName(item)}`}
          onClick={() => onRemoveItem(item)}
        >
          <img className="k-cart-card__remove-icon" src={removeIcon} alt="" />
        </button>
      </li>
    );
  };

  return (
    <div className="k-content-wrapper k-approved-cart">
      <AvailableLimitMeter limit={approvalLimit} used={cartTotal} />

      <h2 className="k-approved-cart__heading">Your cart</h2>

      {isCartEmpty ? (
        <div className="k-approved-cart__empty">
          <img className="k-approved-cart__empty-icon" src={emptyCartIcon} alt="" />
          <p className="k-approved-cart__empty-title">Your cart is empty</p>
          <p className="k-approved-cart__empty-text">Add items below to build your lease</p>
        </div>
      ) : (
        <div className="k-cart-card">
          <ul className="k-cart-card__items">{cartItems.map(renderCartItem)}</ul>
          <div className="k-cart-card__total">
            <span className="k-cart-card__total-label">Total</span>
            <span className="k-cart-card__total-amount">{formatCurrency(cartTotal)}</span>
          </div>
        </div>
      )}

      <h3 className="k-approved-cart__eyebrow">Add an item</h3>

      <div className="k-approved-cart__options">
        {canScanProductTag && (
          <button type="button" className="k-approved-cart__option" onClick={() => history.push(SCAN_PRODUCT_TAG_PATH)}>
            <span className="k-approved-cart__option-title">Scan product tag</span>
            <span className="k-approved-cart__option-text">We read the name and price off the item card</span>
            <span className="k-approved-cart__option-hint">Fastest &mdash; check it, then add</span>
          </button>
        )}
        {isAdding ? (
          <ManualItemForm remaining={remaining} onSave={onAddItem} onCancel={closeEditor} />
        ) : (
          <button
            id={TYPE_IT_IN_BUTTON_ID}
            type="button"
            className="k-approved-cart__option"
            onClick={() => setEditor({ mode: 'add' })}
          >
            <span className="k-approved-cart__option-title">Type it in</span>
            <span className="k-approved-cart__option-text">Enter the name and price yourself</span>
          </button>
        )}
      </div>

      <p className="k-approved-cart__disclaimer">
        Your approval is good until {expiresOn}. Add anything up to {formatCurrencyCompact(remaining)} more, today or
        later.
      </p>

      <div className="k-approved-cart__submit">
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <SubmitButton text="Continue" disabled={isCartEmpty} loading={loading} />
        </form>
      </div>
    </div>
  );
}

export default ApprovedWithCart;
